/**
 * FilePreview — unified preview modal for images, PDF, Word, Excel, text and Markdown.
 *
 * Usage:
 *   import FilePreview, { canPreview } from './FilePreview.jsx';
 *   {open && <FilePreview url={sasUrl} name={fileName} onClose={() => setOpen(false)} />}
 *
 * All file fetching uses plain fetch() — SAS URLs need no auth headers.
 * Heavy libs (mammoth, xlsx, marked) are dynamic imports, loaded only when needed.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { saveUrlToDisk } from '../lib/saveToDisk.js';

// ── Extension helpers ─────────────────────────────────────────────────────────

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'bmp', 'gif', 'webp', 'svg']);
const WORD_EXTS  = new Set(['doc', 'docx']);
const EXCEL_EXTS = new Set(['xls', 'xlsx']);
const TEXT_EXTS  = new Set(['txt', 'md', 'markdown', 'csv', 'log', 'json', 'xml', 'yaml', 'yml']);

function getExt(name = '') {
  return (name.split('.').pop() || '').toLowerCase();
}

/** Returns true if FilePreview can show an in-modal preview for this filename. */
export function canPreview(name) {
  const e = getExt(name);
  return IMAGE_EXTS.has(e) || e === 'pdf' || WORD_EXTS.has(e) || EXCEL_EXTS.has(e) || TEXT_EXTS.has(e);
}

/** Returns true if the file is an image (used to show a thumbnail in the card). */
export function isImageFile(name) {
  return IMAGE_EXTS.has(getExt(name));
}

const PREVIEW_TIMEOUT_MS = 20_000;

function withTimeout(promise, ms, message) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(message)), ms);
    }),
  ]);
}

async function fetchBlob(url) {
  if (!url) throw new Error('No download URL available');
  const res = await withTimeout(fetch(url), PREVIEW_TIMEOUT_MS, 'Download timed out');
  if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
  return withTimeout(res.arrayBuffer(), PREVIEW_TIMEOUT_MS, 'Download timed out');
}

function officePreviewKind(err, buffer) {
  const msg = String(err?.message || err || '').toLowerCase();
  if (buffer?.byteLength >= 4) {
    const view = new Uint8Array(buffer, 0, 4);
    const isZip = view[0] === 0x50 && view[1] === 0x4B;
    const isOle = view[0] === 0xD0 && view[1] === 0xCF && view[2] === 0x11 && view[3] === 0xE0;
    if (isOle || !isZip) return 'password';
  }
  if (/password|encrypt|protected|central directory|invalid signature|unsupported|corrupt/.test(msg)) {
    return 'password';
  }
  return 'generic';
}

// ── Shared loading/error states ───────────────────────────────────────────────

function Spinner({ label = 'Loading preview…' }) {
  return (
    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, textAlign: 'center' }}>
      <div style={{
        width: 28, height: 28, border: '2px solid rgba(255,255,255,0.15)',
        borderTop: '2px solid rgba(255,255,255,0.6)',
        borderRadius: '50%', margin: '0 auto 12px',
        animation: 'fp-spin 0.7s linear infinite',
      }} />
      {label}
      <style>{`@keyframes fp-spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function classifyOfficePreviewError(err, buffer) {
  return officePreviewKind(err, buffer);
}

function PreviewError({ name, detail, url, kind = 'generic' }) {
  const passwordProtected = kind === 'password';
  return (
    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, textAlign: 'center', padding: 32, maxWidth: 520 }}>
      <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }}>⚠</div>
      {passwordProtected ? (
        <>
          <div style={{ color: 'rgba(255,255,255,0.75)', marginBottom: 8 }}>
            <strong>{name}</strong> is password-protected.
          </div>
          <div>It can’t be previewed in the browser. Download it and open in Word to enter the password.</div>
        </>
      ) : (
        <>
          <div>Could not load preview for <strong style={{ color: 'rgba(255,255,255,0.7)' }}>{name}</strong></div>
          {detail && <div style={{ marginTop: 8, fontSize: 11, opacity: 0.6 }}>{detail}</div>}
        </>
      )}
      {url && (
        <a
          href={url}
          download={name}
          style={{
            display: 'inline-block', marginTop: 16,
            color: '#93c5fd', fontSize: 13, textDecoration: 'none',
            padding: '8px 14px', border: '1px solid rgba(147,197,253,0.35)', borderRadius: 6,
          }}
        >
          Download file
        </a>
      )}
    </div>
  );
}

// ── Image ─────────────────────────────────────────────────────────────────────

function ImageViewer({ url, name, displayName }) {
  const [loading, setLoading] = useState(Boolean(url));
  const [error, setError] = useState(url ? null : 'No download URL available');
  const title = displayName || name;

  if (error) return <PreviewError name={title} detail={error} url={url} />;

  return (
    <>
      {loading && <Spinner label="Loading image…" />}
      <img
        src={url}
        alt={title}
        onLoad={() => setLoading(false)}
        onError={() => setError('Could not load image')}
        style={{
          maxWidth: '90vw', maxHeight: '85vh',
          objectFit: 'contain',
          borderRadius: 6,
          boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
          display: loading ? 'none' : 'block',
        }}
      />
    </>
  );
}

// ── PDF ───────────────────────────────────────────────────────────────────────

function PdfViewer({ url }) {
  return (
    <iframe
      src={url}
      title="PDF preview"
      style={{ width: '90vw', height: '85vh', border: 'none', borderRadius: 4, display: 'block' }}
    />
  );
}

// ── Word (doc / docx) ─────────────────────────────────────────────────────────

function WordViewer({ url, name, displayName }) {
  const [html, setHtml]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [errorKind, setErrorKind] = useState('generic');
  const title = displayName || name;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let buf = null;
      try {
        buf = await fetchBlob(url);
        if (officePreviewKind(null, buf) === 'password') {
          throw new Error('Password-protected document');
        }
        const mammoth = await withTimeout(import('mammoth'), PREVIEW_TIMEOUT_MS, 'Preview library timed out');
        const result  = await withTimeout(
          mammoth.convertToHtml({ arrayBuffer: buf }),
          PREVIEW_TIMEOUT_MS,
          'Preview timed out',
        );
        if (!cancelled) setHtml(result.value);
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          setErrorKind(classifyOfficePreviewError(e, buf));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (loading) return <Spinner />;
  if (error)   return <PreviewError name={title} detail={error} url={url} kind={errorKind} />;

  return (
    <iframe
      srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>
        body{font-family:Calibri,Georgia,serif;font-size:14px;line-height:1.75;
             margin:0;padding:32px 48px;color:#1a1a1a;max-width:840px}
        h1,h2,h3{font-family:'Calibri Light',sans-serif}
        table{border-collapse:collapse;width:100%}
        td,th{border:1px solid #ccc;padding:6px 10px}
        img{max-width:100%}
      </style></head><body>${html}</body></html>`}
      sandbox="allow-same-origin"
      title="Word preview"
      style={{ width: '90vw', height: '85vh', border: 'none', background: '#fff',
               borderRadius: 4, display: 'block' }}
    />
  );
}

// ── Excel (xls / xlsx) ────────────────────────────────────────────────────────

function ExcelViewer({ url, name }) {
  const [sheets, setSheets]   = useState([]);
  const [active, setActive]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const buf  = await fetchBlob(url);
        const XLSX = await withTimeout(import('xlsx'), PREVIEW_TIMEOUT_MS, 'Preview library timed out');
        const wb   = await withTimeout(
          Promise.resolve(XLSX.read(buf)),
          PREVIEW_TIMEOUT_MS,
          'Preview timed out',
        );
        const data = wb.SheetNames.map(n => ({
          name: n,
          html: XLSX.utils.sheet_to_html(wb.Sheets[n]),
        }));
        if (!cancelled) setSheets(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (loading) return <Spinner />;
  if (error)   return <PreviewError name={name} detail={error} />;

  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    body{font-family:Calibri,Arial,sans-serif;font-size:12px;margin:0;padding:12px;color:#1a1a1a}
    table{border-collapse:collapse;white-space:nowrap}
    td,th{border:1px solid #d0d0d0;padding:4px 10px;min-width:60px}
    tr:nth-child(even) td{background:#f6f8fa}
    th,tr:first-child td{background:#edf1f5;font-weight:600}
  </style></head><body>${sheets[active]?.html || ''}</body></html>`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '90vw', height: '85vh' }}>
      {/* Sheet tabs */}
      {sheets.length > 1 && (
        <div style={{
          display: 'flex', gap: 3, padding: '6px 8px', flexShrink: 0,
          background: 'rgba(255,255,255,0.07)', borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          {sheets.map((s, i) => (
            <button key={s.name} onClick={() => setActive(i)} style={{
              padding: '3px 14px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
              fontFamily: 'inherit', transition: 'all .15s',
              background:   i === active ? '#fff' : 'rgba(255,255,255,0.08)',
              color:        i === active ? '#111' : 'rgba(255,255,255,0.65)',
              border:       `1px solid ${i === active ? '#fff' : 'rgba(255,255,255,0.15)'}`,
              fontWeight:   i === active ? 600 : 400,
            }}>{s.name}</button>
          ))}
        </div>
      )}
      <iframe
        srcDoc={srcDoc}
        sandbox="allow-same-origin"
        title="Excel preview"
        style={{ flex: 1, border: 'none', background: '#fff', borderRadius: '0 0 4px 4px', display: 'block' }}
      />
    </div>
  );
}

// ── Plain text (txt, csv, log, json, yaml …) ─────────────────────────────────

function TxtViewer({ url, name }) {
  const [text, setText]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        const t = await res.text();
        if (!cancelled) setText(t);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (loading) return <Spinner />;
  if (error)   return <PreviewError name={name} detail={error} />;

  return (
    <pre style={{
      maxWidth: '90vw', maxHeight: '85vh',
      overflow: 'auto',
      margin: 0,
      padding: '28px 36px',
      background: 'rgba(255,255,255,0.04)',
      color: 'rgba(255,255,255,0.85)',
      fontSize: 13,
      lineHeight: 1.7,
      fontFamily: "'SF Mono','Fira Code','Cascadia Code',monospace",
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      borderRadius: 6,
      border: '1px solid rgba(255,255,255,0.08)',
      boxSizing: 'border-box',
    }}>
      {text}
    </pre>
  );
}

// ── Markdown (md) ─────────────────────────────────────────────────────────────

function MdViewer({ url, name }) {
  const [html, setHtml]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res    = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        const text   = await res.text();
        const { marked } = await import('marked');
        if (!cancelled) setHtml(marked(text));
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (loading) return <Spinner />;
  if (error)   return <PreviewError name={name} detail={error} />;

  return (
    <iframe
      srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;
             font-size:14px;line-height:1.75;color:#1e293b;
             margin:0;padding:32px 48px;max-width:860px}
        h1,h2,h3,h4{font-weight:600;margin-top:1.5em;margin-bottom:.5em}
        h1{font-size:1.8em;border-bottom:1px solid #e2e8f0;padding-bottom:.3em}
        h2{font-size:1.4em;border-bottom:1px solid #f1f5f9;padding-bottom:.2em}
        code{background:#f1f5f9;padding:2px 6px;border-radius:4px;
             font-family:'SF Mono','Fira Code',monospace;font-size:.9em}
        pre{background:#f1f5f9;padding:16px;border-radius:8px;overflow:auto}
        pre code{background:none;padding:0}
        blockquote{border-left:4px solid #cbd5e1;margin:0;padding-left:16px;color:#64748b}
        table{border-collapse:collapse;width:100%}
        td,th{border:1px solid #e2e8f0;padding:8px 12px}
        th{background:#f8fafc;font-weight:600}
        tr:nth-child(even) td{background:#f8fafc}
        img{max-width:100%}
        a{color:#0D9488}
        hr{border:none;border-top:1px solid #e2e8f0;margin:2em 0}
      </style></head><body>${html}</body></html>`}
      sandbox="allow-same-origin"
      title="Markdown preview"
      style={{ width: '90vw', height: '85vh', border: 'none', background: '#fff',
               borderRadius: 4, display: 'block' }}
    />
  );
}

// ── File type icon (shown in toolbar) ─────────────────────────────────────────

const _EXT_ICON = {
  jpg: 'jpg', jpeg: 'jpg', png: 'png', gif: 'gif', bmp: 'png', webp: 'png', svg: 'svg',
  pdf: 'pdf',
  doc: 'doc', docx: 'doc',
  xls: 'xls', xlsx: 'xls', csv: 'xls',
  txt: 'txt', xml: 'xml',
  zip: 'zip', rar: 'rar',
  mp3: 'mp3', wav: 'wav',
  ppt: 'ppt', pptx: 'ppt',
};

function ToolbarIcon({ name }) {
  const slug = _EXT_ICON[getExt(name)];
  if (slug) {
    return <img src={`/filetypes/${slug}.svg`} alt="" style={{ width: 22, height: 22, flexShrink: 0 }} />;
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
      style={{ width: 22, height: 22, flexShrink: 0, opacity: 0.6 }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  );
}

// ── Main FilePreview modal ────────────────────────────────────────────────────

export default function FilePreview({ url, name, displayName, onClose }) {
  const e = getExt(name);
  const title = displayName || name;
  const close = useCallback(() => onClose(), [onClose]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const onKey = ev => { if (ev.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  // Fetch → object URL so the file saves to disk; <a download> alone is
  // ignored cross-origin and would open the SAS URL in a new tab.
  async function download(ev) {
    ev.stopPropagation();
    if (downloading || !url) return;
    setDownloading(true);
    try {
      await saveUrlToDisk(url, name);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');  // last-resort fallback
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div
      onClick={close}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Toolbar */}
      <div
        onClick={ev => ev.stopPropagation()}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.45)',
        }}
      >
        <ToolbarIcon name={name} />
        <span style={{
          color: '#fff', flex: 1, fontSize: 13, fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {title}
        </span>
        <button
          type="button"
          onClick={download}
          disabled={downloading || !url}
          style={{
            color: 'rgba(255,255,255,0.8)', fontSize: 12, cursor: downloading ? 'wait' : 'pointer',
            padding: '5px 12px', border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 6, whiteSpace: 'nowrap', background: 'rgba(255,255,255,0.07)',
          }}
        >
          {downloading ? 'Downloading…' : '↓ Download'}
        </button>
        <button
          onClick={close}
          aria-label="Close"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#fff', fontSize: 22, lineHeight: 1, padding: '0 4px', opacity: 0.7,
          }}
        >×</button>
      </div>

      {/* Preview content — clicking the dark area around the file closes the
          modal; clicks on the file itself (img/iframe/table) must not. */}
      <div
        onClick={ev => { ev.stopPropagation(); if (ev.target === ev.currentTarget) close(); }}
        style={{
          flex: 1, overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: IMAGE_EXTS.has(e) ? 24 : 0,
        }}
      >
        {!url ? (
          <PreviewError name={title} detail="No download URL available" />
        ) : (
          <>
            {IMAGE_EXTS.has(e)                      && <ImageViewer url={url} name={name} displayName={title} />}
            {e === 'pdf'                             && <PdfViewer   url={url} />}
            {WORD_EXTS.has(e)                        && <WordViewer  url={url} name={name} displayName={title} />}
            {EXCEL_EXTS.has(e)                       && <ExcelViewer url={url} name={name} />}
            {(e === 'md' || e === 'markdown')         && <MdViewer    url={url} name={name} />}
            {TEXT_EXTS.has(e) && e !== 'md' && e !== 'markdown' && <TxtViewer url={url} name={name} />}
          </>
        )}
      </div>
    </div>
  );
}
